/**
 * Employment Legend Configuration
 *
 * Defines legend specifications for all employment layers
 * including color stops, labels, and units
 */

export interface LegendStop {
  value: number
  color: string
  label: string
}

export interface LegendCategory {
  value: number
  color: string
  label: string
}

export interface GradientLegendConfig {
  type: 'gradient'
  label: string
  unit: string
  stops: LegendStop[]
}

export interface CategoricalLegendConfig {
  type: 'categorical'
  label: string
  categories: LegendCategory[]
}

export type EmploymentLegendConfig =
  | GradientLegendConfig
  | CategoricalLegendConfig

/**
 * Legend configurations for all employment layers
 */
export const EMPLOYMENT_LEGEND_CONFIG: Record<string, EmploymentLegendConfig> =
  {
    'employment-density': {
      type: 'gradient',
      label: 'Employment Density',
      unit: 'jobs/km²',
      stops: [
        { value: 0, color: '#FFFFCC', label: '0' },
        { value: 100, color: '#FFEDA0', label: '100' },
        { value: 500, color: '#FED976', label: '500' },
        { value: 1000, color: '#FD8D3C', label: '1K' },
        { value: 2000, color: '#E31A1C', label: '2K' },
        { value: 5000, color: '#B10026', label: '5K+' },
      ],
    },

    'employment-growth-rate': {
      type: 'gradient',
      label: 'Employment Growth (5-year)',
      unit: '%/year',
      stops: [
        { value: -10, color: '#D7191C', label: '-10%' },
        { value: -5, color: '#FDAE61', label: '-5%' },
        { value: 0, color: '#FFFFBF', label: '0%' },
        { value: 5, color: '#A6D96A', label: '+5%' },
        { value: 10, color: '#1A9641', label: '+10%+' },
      ],
    },

    'employment-fulltime-ratio': {
      type: 'gradient',
      label: 'Full-Time Employment Ratio',
      unit: '%',
      stops: [
        { value: 0, color: '#EDFDE7', label: '0%' },
        { value: 40, color: '#BAE4B3', label: '40%' },
        { value: 60, color: '#74C476', label: '60%' },
        { value: 80, color: '#31A354', label: '80%' },
        { value: 100, color: '#006D2C', label: '100%' },
      ],
    },

    'employment-jobs-per-establishment': {
      type: 'gradient',
      label: 'Jobs per Establishment',
      unit: 'jobs/est.',
      stops: [
        { value: 0, color: '#EFF3FF', label: '0' },
        { value: 5, color: '#BDD7E7', label: '5' },
        { value: 10, color: '#6BAED6', label: '10' },
        { value: 20, color: '#3182BD', label: '20' },
        { value: 50, color: '#08519C', label: '50' },
        { value: 100, color: '#08306B', label: '100+' },
      ],
    },

    'employment-dominant-sector': {
      type: 'categorical',
      label: 'Dominant Employment Sector',
      categories: [
        { value: 1, color: '#4DAF4A', label: 'Primary' },
        { value: 2, color: '#FF7F00', label: 'Secondary' },
        { value: 3, color: '#377EB8', label: 'Tertiary' },
      ],
    },

    'employment-public-private': {
      type: 'categorical',
      label: 'Public vs Private Sector',
      categories: [
        { value: 1, color: '#984EA3', label: 'Public Dominant' },
        { value: 2, color: '#999999', label: 'Balanced' },
        { value: 3, color: '#377EB8', label: 'Private Dominant' },
      ],
    },
  }

/**
 * Get legend configuration for a specific employment layer
 */
export function getEmploymentLegendConfig(
  layerId: string
): EmploymentLegendConfig | undefined {
  return EMPLOYMENT_LEGEND_CONFIG[layerId]
}

/**
 * Check if a layer has a gradient (continuous) legend
 */
export function isGradientLegend(
  config: EmploymentLegendConfig
): config is GradientLegendConfig {
  return config.type === 'gradient'
}

/**
 * Check if a layer has a categorical (discrete) legend
 */
export function isCategoricalLegend(
  config: EmploymentLegendConfig
): config is CategoricalLegendConfig {
  return config.type === 'categorical'
}
